import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  AlertTriangle,
  CheckCircle,
  Filter,
  MapPin,
  Clock,
  Activity,
  Droplets,
  BrainCircuit,
  Satellite,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ALERTS, ALERT_COLORS, ALERT_LEVEL_LABELS, ALERT_BG } from '@/lib/mockData'
import { formatDate, timeAgo } from '@/lib/utils'
import type { Alert, AlertLevel, RiskCategory } from '@/types'

// ----------------------------------------------------------
// Category icon
// ----------------------------------------------------------

function CategoryIcon({ category }: { category: RiskCategory }) {
  const map: Record<RiskCategory, { icon: React.ComponentType<{ size?: number | string; className?: string }>; label: string; colorClass: string }> = {
    landslide: { icon: Activity, label: 'Frana', colorClass: 'text-terra-orange' },
    flood: { icon: Droplets, label: 'Alluvione', colorClass: 'text-terra-blue' },
    erosion: { icon: Satellite, label: 'InSAR', colorClass: 'text-terra-purple' },
    combined: { icon: BrainCircuit, label: 'Combinato', colorClass: 'text-slate-400' },
  }
  const { icon: Icon, label, colorClass } = map[category]
  return (
    <span className={cn('flex items-center gap-1 text-xs', colorClass)}>
      <Icon size={11} />
      {label}
    </span>
  )
}

// ----------------------------------------------------------
// Alert detail modal
// ----------------------------------------------------------

function AlertModal({ alert, onClose }: { alert: Alert; onClose: () => void }) {
  const color = ALERT_COLORS[alert.level]

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.2 }}
        className="glass-strong rounded-2xl p-6 max-w-lg w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: color + '20', border: `1px solid ${color}40` }}
            >
              <AlertTriangle size={18} style={{ color }} />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">{alert.title}</h2>
              <p className="text-xs text-slate-400">{alert.municipality}, {alert.province}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Level badge */}
        <div className={cn('inline-flex items-center gap-2 border rounded-lg px-3 py-1.5 mb-4', ALERT_BG[alert.level])}>
          <div className="w-2 h-2 rounded-full" style={{ background: color }} />
          <span className="text-sm font-bold">{ALERT_LEVEL_LABELS[alert.level]}</span>
          <span className="text-xs opacity-70">({alert.level})</span>
        </div>

        {/* Description */}
        <div className="bg-slate-900/60 rounded-lg p-4 mb-4">
          <p className="text-sm text-slate-200 leading-relaxed">{alert.description}</p>
        </div>

        {/* Metadata */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {[
            { label: 'Categoria', value: alert.category },
            { label: 'Stato', value: alert.status === 'resolved' ? 'Risolto' : alert.status === 'acknowledged' ? 'Notificato' : 'Attivo' },
            { label: 'Creato', value: formatDate(alert.created_at) },
            { label: 'ID Allerta', value: alert.id },
          ].map(({ label, value }) => (
            <div key={label} className="bg-slate-900/40 rounded-lg p-3">
              <div className="text-xs text-slate-500 mb-1">{label}</div>
              <div className="text-xs font-medium text-white capitalize">{value}</div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          {alert.status !== 'resolved' && (
            <button className="flex-1 btn-primary justify-center">
              <CheckCircle size={14} />
              Risolvi Allerta
            </button>
          )}
          <button onClick={onClose} className="flex-1 btn-ghost justify-center border border-slate-700/60">
            Chiudi
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

// ----------------------------------------------------------
// Alert row
// ----------------------------------------------------------

function AlertRow({ alert, onClick }: { alert: Alert; onClick: () => void }) {
  const color = ALERT_COLORS[alert.level]

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 10 }}
      className={cn(
        'terra-card cursor-pointer transition-all duration-200 hover:border-slate-600/60',
        alert.status === 'resolved' && 'opacity-50'
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-4">
        {/* Color indicator */}
        <div
          className="w-1 self-stretch rounded-full flex-shrink-0 min-h-12"
          style={{ background: color }}
        />

        {/* Main content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={cn('border text-xs font-bold px-2 py-0.5 rounded flex-shrink-0', ALERT_BG[alert.level])}>
                  {alert.level}
                </span>
                <h3 className="text-sm font-semibold text-white truncate">{alert.title}</h3>
              </div>
              <p className="text-xs text-slate-300 line-clamp-2">{alert.description}</p>
            </div>

            {/* Status */}
            <div className="flex-shrink-0 text-right">
              <span className={cn(
                'text-xs font-medium px-2 py-1 rounded border',
                alert.status === 'resolved'
                  ? 'bg-terra-green/10 text-terra-green border-terra-green/20'
                  : alert.status === 'acknowledged'
                  ? 'bg-slate-800 text-slate-400 border-slate-700/60'
                  : 'bg-terra-red/10 text-terra-red border-terra-red/20'
              )}>
                {alert.status === 'resolved' ? 'Risolto' : alert.status === 'acknowledged' ? 'Notificato' : 'Attivo'}
              </span>
            </div>
          </div>

          {/* Footer meta */}
          <div className="flex items-center gap-4 mt-2">
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <MapPin size={10} />
              {alert.municipality}, {alert.province}
            </span>
            <CategoryIcon category={alert.category} />
            <span className="flex items-center gap-1 text-xs text-slate-600 ml-auto">
              <Clock size={10} />
              {timeAgo(alert.created_at)}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

// ----------------------------------------------------------
// Stats summary bar
// ----------------------------------------------------------

function AlertSummaryBar({ alerts }: { alerts: Alert[] }) {
  const byLevel = (['P4', 'P3', 'P2', 'P1'] as AlertLevel[]).map((l) => ({
    level: l,
    count: alerts.filter((a) => a.level === l && a.status !== 'resolved').length,
  }))

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {byLevel.map(({ level, count }) => {
        const color = ALERT_COLORS[level]
        return (
          <motion.div
            key={level}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass rounded-xl p-4 text-center"
            style={{ borderColor: color + '25' }}
          >
            <div className="text-2xl font-bold" style={{ color }}>
              {count}
            </div>
            <div className="text-xs text-slate-400 mt-1">{ALERT_LEVEL_LABELS[level]}</div>
            <div className="text-xs text-slate-600">{level}</div>
          </motion.div>
        )
      })}
    </div>
  )
}

// ----------------------------------------------------------
// Filter bar
// ----------------------------------------------------------

type FilterLevel = AlertLevel | 'ALL'

interface AlertFilters {
  level: FilterLevel
  status: 'all' | 'active' | 'resolved'
}

// ----------------------------------------------------------
// Page
// ----------------------------------------------------------

export default function AlertCenterPage() {
  const [filters, setFilters] = useState<AlertFilters>({ level: 'ALL', status: 'active' })
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null)

  const LEVEL_ORDER: Record<AlertLevel, number> = { P4: 0, P3: 1, P2: 2, P1: 3 }

  const filteredAlerts = ALERTS.filter((a) => {
    if (filters.level !== 'ALL' && a.level !== filters.level) return false
    if (filters.status === 'active' && a.status === 'resolved') return false
    if (filters.status === 'resolved' && a.status !== 'resolved') return false
    return true
  }).sort((a, b) => {
    if (LEVEL_ORDER[a.level] !== LEVEL_ORDER[b.level]) return LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level]
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">Centro Allerte</h1>
            <p className="text-sm text-slate-400 mt-0.5">
              Monitoraggio allerte idrogeologiche · {ALERTS.filter((a) => a.status !== 'resolved').length} attive
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-terra-red animate-pulse" />
            <span className="text-xs text-terra-red font-medium">Live</span>
          </div>
        </div>
      </motion.div>

      {/* Summary cards */}
      <AlertSummaryBar alerts={ALERTS} />

      {/* Filter bar */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex items-center gap-3 flex-wrap"
      >
        <Filter size={14} className="text-slate-500 flex-shrink-0" />

        {/* Level filter */}
        <div className="flex gap-1 p-1 glass rounded-lg">
          {(['ALL', 'P4', 'P3', 'P2', 'P1'] as FilterLevel[]).map((l) => (
            <button
              key={l}
              onClick={() => setFilters((f) => ({ ...f, level: l }))}
              className={cn(
                'px-3 py-1 rounded text-xs font-medium transition-all',
                filters.level === l
                  ? l === 'ALL'
                    ? 'bg-terra-blue/20 text-terra-blue border border-terra-blue/30'
                    : `text-white border`
                  : 'text-slate-400 hover:text-slate-200'
              )}
              style={
                filters.level === l && l !== 'ALL'
                  ? {
                      background: ALERT_COLORS[l] + '20',
                      borderColor: ALERT_COLORS[l] + '40',
                      color: ALERT_COLORS[l],
                    }
                  : {}
              }
            >
              {l === 'ALL' ? 'Tutti' : l}
            </button>
          ))}
        </div>

        {/* Status filter */}
        <div className="flex gap-1 p-1 glass rounded-lg">
          {(['all', 'active', 'resolved'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilters((f) => ({ ...f, status: s }))}
              className={cn(
                'px-3 py-1 rounded text-xs font-medium transition-all',
                filters.status === s
                  ? 'bg-slate-700 text-slate-200'
                  : 'text-slate-500 hover:text-slate-300'
              )}
            >
              {s === 'all' ? 'Tutti' : s === 'active' ? 'Attivi' : 'Risolti'}
            </button>
          ))}
        </div>

        <span className="text-xs text-slate-600 ml-auto">
          {filteredAlerts.length} risultati
        </span>
      </motion.div>

      {/* Alert list */}
      <div className="space-y-3">
        <AnimatePresence mode="popLayout">
          {filteredAlerts.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="terra-card py-16 text-center"
            >
              <CheckCircle size={32} className="text-terra-green mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-300">Nessuna allerta trovata</p>
              <p className="text-xs text-slate-500 mt-1">Modifica i filtri per visualizzare altri risultati</p>
            </motion.div>
          ) : (
            filteredAlerts.map((alert) => (
              <AlertRow
                key={alert.id}
                alert={alert}
                onClick={() => setSelectedAlert(alert)}
              />
            ))
          )}
        </AnimatePresence>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selectedAlert && (
          <AlertModal
            alert={selectedAlert}
            onClose={() => setSelectedAlert(null)}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
